import { KeyboardEvent } from 'react';
import { utils } from '@kurtosys/ksys-app-template';
import { Loggable, LoggableContext } from '../Loggable';
import { KEYBOARD_KEYS, KEYBOARD_STATE_KEY } from '../../constants';

interface EventOptions {
    preventDefault?: boolean;
    stopPropagation?: boolean;
}

interface EventDirectionOptions {
    down?: EventOptions;
    up?: EventOptions;
}

interface KeyboardEventContext extends LoggableContext {
    defaults?: {
        accept?: EventDirectionOptions;
        escape?: EventDirectionOptions;
        navigation?: EventDirectionOptions;
        printable?: EventDirectionOptions;
        invalid?: EventDirectionOptions;
    };
}

interface EventHandlerOptions<T> {
    handler: EventHandler<T>;
    options?: EventOptions;
}

interface EventHandlers<T> {
    [key: string]: {
        down?: EventHandlerOptions<T>;
        up?: EventHandlerOptions<T>;
    };
}

interface StateKeyGroup {
    pre: KEYBOARD_STATE_KEY;
    post: KEYBOARD_STATE_KEY;
    target?: KEYBOARD_STATE_KEY;
}

interface EventTargetBase {
    type: 'state' | 'key';
    direction: EventDirection;
}

interface EventTargetStateKey extends EventTargetBase {
    type: 'state';
    key: KEYBOARD_STATE_KEY;
}

interface EventTargetKey extends EventTargetBase {
    type: 'key';
    key: KEYBOARD_KEYS;
}
type EventTarget = EventTargetKey | EventTargetStateKey;

type EventHandler<T> = (e: KeyboardEvent<T>) => void;

export type EventDirection = 'up' | 'down';

type EventState = 'accept' | 'escape' | 'navigation' | 'printable' | 'invalid';

type EventFn<T> = (e: KeyboardEvent<T>) => void;

export class KeyboardEvents<HTMLElementType> extends Loggable<
    KeyboardEventContext
> {
    private static navKeys: Array<KEYBOARD_KEYS> = [
        KEYBOARD_KEYS.LEFT_ARROW,
        KEYBOARD_KEYS.RIGHT_ARROW,
        KEYBOARD_KEYS.UP_ARROW,
        KEYBOARD_KEYS.DOWN_ARROW,
        KEYBOARD_KEYS.HOME,
        KEYBOARD_KEYS.END,
        KEYBOARD_KEYS.PAGE_UP,
        KEYBOARD_KEYS.PAGE_DOWN,
    ];
    private static acceptKeys: Array<KEYBOARD_KEYS> = [
        KEYBOARD_KEYS.ENTER,
        KEYBOARD_KEYS.SPACE,
        KEYBOARD_KEYS.SPACEBAR,
    ];
    private static escapeKeys: Array<KEYBOARD_KEYS> = [
        KEYBOARD_KEYS.ESCAPE,
        KEYBOARD_KEYS.TAB,
    ];
    protected className = 'KeyboardEvents';
    private keyEvents: EventHandlers<HTMLElementType> = {
        [KEYBOARD_KEYS.LEFT_ARROW]: null,
        [KEYBOARD_KEYS.RIGHT_ARROW]: null,
        [KEYBOARD_KEYS.UP_ARROW]: null,
        [KEYBOARD_KEYS.DOWN_ARROW]: null,
        [KEYBOARD_KEYS.HOME]: null,
        [KEYBOARD_KEYS.END]: null,
        [KEYBOARD_KEYS.PAGE_UP]: null,
        [KEYBOARD_KEYS.PAGE_DOWN]: null,
        [KEYBOARD_KEYS.ENTER]: null,
        [KEYBOARD_KEYS.SPACE]: null,
        [KEYBOARD_KEYS.SPACEBAR]: null,
        [KEYBOARD_KEYS.ESCAPE]: null,
        [KEYBOARD_KEYS.TAB]: null,
        [KEYBOARD_KEYS.BACKSPACE]: null,
    };
    private stateEvents: EventHandlers<HTMLElementType> = {
        preNavigation: null,
        postNavigation: null,
        preAccept: null,
        accept: null,
        postAccept: null,
        preEscape: null,
        escape: null,
        postEscape: null,
        prePrintable: null,
        printable: null,
        postPrintable: null,
        invalidKey: null,
    };
    constructor(context: KeyboardEventContext) {
        super(context);
    }
    public bindKeyEvent(
        key: KEYBOARD_KEYS,
        direction: EventDirection,
        fn: EventHandler<HTMLElementType>,
        options?: EventOptions
    ) {
        if (this.keyEvents[key] !== undefined) {
            if (this.keyEvents[key] === null) {
                this.keyEvents[key] = {};
            }
            this.keyEvents[key][direction] = {
                options,
                handler: fn,
            };
        } else {
            this.warn(`cannot bind unknown key: ${key}`);
        }
    }
    public bindStateEvent(
        stateKey: KEYBOARD_STATE_KEY,
        direction: EventDirection,
        fn: EventHandler<HTMLElementType>,
        options?: EventOptions
    ) {
        if (this.stateEvents[stateKey] !== undefined) {
            if (this.stateEvents[stateKey] === null) {
                this.stateEvents[stateKey] = {};
            }
            this.stateEvents[stateKey][direction] = {
                options,
                handler: fn,
            };
        } else {
            this.warn(`cannot bind unknown state key: ${stateKey}`);
        }
    }
    public bindEventToMany(
        targets: Array<EventTarget>,
        fn: EventHandler<HTMLElementType>,
        options?: EventOptions
    ) {
        for (const target of targets) {
            if (target.type === 'state') {
                this.bindStateEvent(target.key, target.direction, fn, options);
            } else if (target.type === 'key') {
                this.bindKeyEvent(target.key, target.direction, fn, options);
            }
        }
    }
    /**
     * Returns the event handler for either onKeyUp or onKeyDown props,
     * with the option to further limit what what event states to use
     * @title Handle Keyboard Events for
     */
    public handleKeyboardEventsFor = (
        onKeyType: EventDirection,
        states?: Array<EventState>
    ): EventFn<HTMLElementType> => {
        return e => {
            const { key } = e;
            this.debug('--------------------------------');
            this.debug(
                `processing onKey${onKeyType}{${key}} event for`,
                states
            );
            if (
                states === undefined ||
                this.isKeyInAllowedState(key as KEYBOARD_KEYS, states)
            ) {
                this.handleKeyboardEvent(e, onKeyType);
            } else {
                this.executeStateEvent(
                    KEYBOARD_STATE_KEY.INVALID_KEY,
                    key,
                    e,
                    onKeyType
                );
            }
        };
    };
    public get onKeyProps() {
        return {
            onKeyDown: this.handleKeyboardEventsFor('down'),
            onKeyUp: this.handleKeyboardEventsFor('up'),
        };
    }
    protected handleKeyboardEvent = (
        e: KeyboardEvent<HTMLElementType>,
        eventDirection: EventDirection
    ) => {
        const { key } = e;
        this.debug(`handleKeyboardEvent key{${key}}`);
        const { pre, post, target } = this.getStateKeys(key as KEYBOARD_KEYS);
        const isKeyBound = this.isKeyBoundToAnEvent(eventDirection, key);
        if (isKeyBound || target !== undefined) {
            this.executeStateEvent(pre, key, e, eventDirection);
            // KEY events override state events
            if (isKeyBound) {
                this.executeKeyEvent(target, key, e, eventDirection);
            } else {
                this.executeStateEvent(target, key, e, eventDirection);
            }
            this.executeStateEvent(post, key, e, eventDirection);
        }
    };
    private executeKeyEvent(
        stateKey: KEYBOARD_STATE_KEY | undefined,
        key: string | undefined,
        e: KeyboardEvent<HTMLElementType>,
        eventDirection: EventDirection
    ) {
        if (key && this.isKeyBoundToAnEvent(eventDirection, key)) {
            this.debugSuccess(`executing key{${key}} event`);
            this.processEventOptions(e, eventDirection, stateKey, key);
            if (this.keyEvents[key][eventDirection].handler) {
                this.keyEvents[key][eventDirection].handler(e);
            }
        }
    }
    private executeStateEvent(
        stateKey: KEYBOARD_STATE_KEY | undefined,
        key: string | undefined,
        e: KeyboardEvent<HTMLElementType>,
        eventDirection: EventDirection
    ) {
        if (
            stateKey &&
            this.isStateKeyBoundToAnEvent(eventDirection, stateKey)
        ) {
            this.debugSuccess(`executing state key{${stateKey}} event`);
            this.processEventOptions(e, eventDirection, stateKey, key);
            if (this.stateEvents[stateKey][eventDirection].handler) {
                this.stateEvents[stateKey][eventDirection].handler(e);
            }
        }
    }
    private processEventOptions(
        e: KeyboardEvent<HTMLElementType>,
        eventDirection: EventDirection,
        stateKey: KEYBOARD_STATE_KEY | undefined,
        key: string | undefined
    ) {
        const options = this.getEventOptions(eventDirection, stateKey, key);
        this.debugInfo(`    using event options`, options);
        if (options) {
            if (options.preventDefault) {
                e.preventDefault();
            }
            if (options.stopPropagation) {
                e.stopPropagation();
            }
        }
    }
    private getEventOptions(
        eventDirection: EventDirection,
        stateKey: KEYBOARD_STATE_KEY | undefined,
        key: string | undefined
    ) {
        const defaultOptions = this.getDefaultOptionsForKey(
            eventDirection,
            key
        );
        const stateOptions = this.getStateKeyOptions(eventDirection, stateKey);
        const keyOptions = this.getKeyOptions(eventDirection, key);
        return { ...defaultOptions, ...stateOptions, ...keyOptions };
    }
    private getKeyOptions(eventDirection: EventDirection, key?: string) {
        if (
            key &&
            this.keyEvents &&
            this.keyEvents[key] &&
            this.keyEvents[key][eventDirection] &&
            this.keyEvents[key][eventDirection].options
        ) {
            return this.keyEvents[key][eventDirection].options;
        }
        return {};
    }
    private getStateKeyOptions(eventDirection: EventDirection, key?: string) {
        if (
            key &&
            this.stateEvents &&
            this.stateEvents[key] &&
            this.stateEvents[key][eventDirection] &&
            this.stateEvents[key][eventDirection].options
        ) {
            return this.stateEvents[key][eventDirection].options;
        }
        return {};
    }
    private getDefaultOptionsForKey(
        eventDirection: EventDirection,
        key: string | undefined
    ): EventOptions | undefined {
        if (key) {
            const eventState = this.getKeyEventState(key);
            if (
                this.context.defaults &&
                this.context.defaults[eventState] &&
                this.context.defaults[eventState][eventDirection]
            ) {
                return this.context.defaults[eventState][eventDirection];
            }
        }
        return {};
    }
    private isKeyInAllowedState(key: KEYBOARD_KEYS, states: Array<EventState>) {
        const eventState = this.getKeyEventState(key);
        if (states.indexOf(eventState) >= 0) {
            return true;
        }
        return false;
    }
    private getKeyEventState(key: string | undefined): EventState {
        if (key) {
            if (this.isNavigationEvent(key)) {
                return 'navigation';
            } else if (this.isAcceptEvent(key)) {
                return 'accept';
            } else if (this.isEscapeEvent(key)) {
                return 'escape';
            } else if (this.isPrintableEvent(key)) {
                return 'printable';
            }
        }
        return 'invalid';
    }
    private getStateKeys(key: KEYBOARD_KEYS): StateKeyGroup {
        let preStateKey: KEYBOARD_STATE_KEY;
        let targetStateKey: KEYBOARD_STATE_KEY;
        let postStateKey: KEYBOARD_STATE_KEY;
        if (this.isEscapeEvent(key)) {
            this.debug(`is escape event`);
            preStateKey = KEYBOARD_STATE_KEY.PRE_ESCAPE;
            targetStateKey = KEYBOARD_STATE_KEY.ESCAPE;
            postStateKey = KEYBOARD_STATE_KEY.POST_ESCAPE;
        } else if (this.isNavigationEvent(key)) {
            this.debug(`is navigation event`);
            preStateKey = KEYBOARD_STATE_KEY.PRE_NAVIGATION;
            postStateKey = KEYBOARD_STATE_KEY.POST_NAVIGATION;
        } else if (this.isAcceptEvent(key)) {
            this.debug(`is accept event`);
            preStateKey = KEYBOARD_STATE_KEY.PRE_ACCEPT;
            targetStateKey = KEYBOARD_STATE_KEY.ACCEPT;
            postStateKey = KEYBOARD_STATE_KEY.POST_ACCEPT;
        } else if (this.isPrintableEvent(key)) {
            this.debug(`is printable event`);
            preStateKey = KEYBOARD_STATE_KEY.PRE_PRINTABLE;
            targetStateKey = KEYBOARD_STATE_KEY.PRINTABLE;
            postStateKey = KEYBOARD_STATE_KEY.POST_PRINTABLE;
        } else {
            this.debug(`is unhandled/invalid event`);
            targetStateKey = KEYBOARD_STATE_KEY.INVALID_KEY;
        }
        return {
            pre: preStateKey,
            target: targetStateKey,
            post: postStateKey,
        };
    }
    private isStateKeyBoundToAnEvent(
        eventDirection: EventDirection,
        stateKey: KEYBOARD_STATE_KEY
    ) {
        return (
            !utils.typeChecks.isNullOrUndefined(this.stateEvents[stateKey]) &&
            !utils.typeChecks.isNullOrUndefined(
                this.stateEvents[stateKey][eventDirection]
            )
        );
    }
    private isKeyBoundToAnEvent(eventDirection: EventDirection, key: string) {
        return (
            !utils.typeChecks.isNullOrUndefined(this.keyEvents[key]) &&
            !utils.typeChecks.isNullOrUndefined(
                this.keyEvents[key][eventDirection]
            )
        );
    }
    private isEscapeEvent(key: string): boolean {
        return KeyboardEvents.escapeKeys.indexOf(key as KEYBOARD_KEYS) !== -1;
    }
    private isNavigationEvent(key: string): boolean {
        return KeyboardEvents.navKeys.indexOf(key as KEYBOARD_KEYS) !== -1;
    }
    private isAcceptEvent(key: string): boolean {
        return KeyboardEvents.acceptKeys.indexOf(key as KEYBOARD_KEYS) !== -1;
    }
    private isPrintableEvent(key: string): boolean {
        const reg = new RegExp('[a-zA-Z0-9]');
        return key.length === 1 && key.match(reg) !== null;
    }
}



// WEBPACK FOOTER //
// ./src/utils/accessibility/KeyboardEvents.ts